// Presentational radar chart — the SVG spider polygon, shared by the Radar
// widget (position templates, with a dropdown) and the Passing widgets (the
// fixed skill-based "passing" template, with a peer-average overlay). All
// geometry/scaling lives in the pure, unit-tested radar.ts; this is just the
// view over a resolved metric list + row(s).

import { theme } from "../lib/theme.js";
import { buildRadar, type RadarMetric } from "./radar.js";

const RINGS = [0.25, 0.5, 0.75, 1];
const LINE = "rgba(148,163,184,0.28)";
// Peer-average overlay colour — amber, distinct from the player's accent blue
// and readable on the dark tile. The player polygon stays the filled focus; the
// peer average is a thin outline reference.
const PEER = "#fbbf24";

export interface CompareSeries {
  row: Record<string, unknown> | null | undefined;
  label: string;
}

export default function RadarChart({
  metrics,
  row,
  ariaLabel,
  view = 320,
  radius = 108,
  compare,
  playerLabel = "Player",
}: {
  metrics: RadarMetric[];
  row: Record<string, unknown> | null | undefined;
  ariaLabel: string;
  view?: number;
  radius?: number;
  /** Optional second polygon (e.g. the position-group average) + a legend. */
  compare?: CompareSeries;
  playerLabel?: string;
}) {
  const cx = view / 2;
  const cy = view / 2;
  const shape = buildRadar(metrics, row, cx, cy, radius);
  const compareShape = compare ? buildRadar(metrics, compare.row, cx, cy, radius) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minHeight: 0, gap: 6 }}>
      <svg
        viewBox={`0 0 ${view} ${view}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ flex: 1, minHeight: 0, width: "100%" }}
        role="img"
        aria-label={ariaLabel}
      >
        {/* Rings */}
        {RINGS.map((f) => (
          <circle key={f} cx={cx} cy={cy} r={radius * f} fill="none" stroke={LINE} strokeWidth={0.5} />
        ))}
        {/* Spokes */}
        {shape.axes.map((a) => (
          <line key={a.key} x1={cx} y1={cy} x2={a.labelAt.x} y2={a.labelAt.y} stroke={LINE} strokeWidth={0.4} />
        ))}
        {/* Peer-average overlay (drawn under the player polygon): thin outline.
            Polygons are pointer-events:none so hovering always hits the vertex
            dots (incl. the peer dots the player polygon overlaps) — otherwise the
            player polygon's fill swallowed the peer-average tooltips. */}
        {compareShape && (
          <>
            <polygon points={compareShape.polygon} fill="none" stroke={PEER} strokeWidth={1.2} strokeDasharray="3 2" strokeLinejoin="round" pointerEvents="none" />
            {compareShape.axes.map((a) =>
              a.norm === null ? null : (
                <circle key={a.key} cx={a.vertex.x} cy={a.vertex.y} r={1.8} fill={PEER}>
                  <title>{vertexTitle(`${compare!.label} · ${a.label}`, a.value, a.norm)}</title>
                </circle>
              ),
            )}
          </>
        )}
        {/* Player polygon (pointer-events:none — see the overlay note above) */}
        <polygon
          points={shape.polygon}
          fill={theme.accent}
          fillOpacity={0.22}
          stroke={theme.accent}
          strokeWidth={1.5}
          strokeLinejoin="round"
          pointerEvents="none"
        />
        {/* Player vertices */}
        {shape.axes.map((a) =>
          a.norm === null ? null : (
            <circle key={a.key} cx={a.vertex.x} cy={a.vertex.y} r={2.4} fill={theme.accent} stroke={theme.tile} strokeWidth={0.6}>
              <title>{vertexTitle(a.label, a.value, a.norm)}</title>
            </circle>
          ),
        )}
        {/* Axis labels */}
        {shape.axes.map((a) => (
          <text
            key={a.key}
            x={a.labelAt.x}
            y={a.labelAt.y}
            textAnchor={a.textAnchor}
            dominantBaseline="middle"
            fontSize={8}
            fill={a.norm === null ? theme.inkFaint : theme.inkMuted}
          >
            {a.label}
          </text>
        ))}
      </svg>
      {compare && (
        <div style={{ display: "flex", justifyContent: "center", gap: 16, fontSize: 11, color: theme.inkMuted }}>
          <Key color={theme.accent} label={playerLabel} />
          <Key color={PEER} label={compare.label} dashed />
        </div>
      )}
    </div>
  );
}

function Key({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 14,
          height: 0,
          borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}`,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

/** Tooltip: metric, its value, and where it sits on the axis (0–100% of range). */
function vertexTitle(label: string, value: number | null, norm: number): string {
  const v = value === null ? "—" : Number.isInteger(value) ? String(value) : value.toFixed(2);
  return `${label}: ${v} · ${Math.round(norm * 100)}% of range`;
}
